import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LandingPage.css';

// ── Interactive Culture Data ──
const WEDDING_CULTURES: Record<string, { name: string, description: string, rituals: string[] }> = {
    punjabi: {
        name: 'Punjabi',
        description: 'Vibrant, energetic, and full of life. Plan the perfect Dhol night and Anand Karaj.',
        rituals: ['Roka & Thaka', 'Chunni Ceremony', 'Sangeet & Mehendi', 'Chooda & Kalire', 'Anand Karaj']
    },
    marathi: {
        name: 'Marathi',
        description: 'Rooted in deep tradition and simple elegance. Perfect the Maharashtrian rituals.',
        rituals: ['Sakharpuda', 'Kelvan', 'Halad Chadavane', 'Antarpat', 'Saptapadi']
    },
    bengali: {
        name: 'Bengali',
        description: 'Artistic, cultural, and profoundly beautiful. Organize every detail from Ashirbad to Sindoor Daan.',
        rituals: ['Ashirbad', 'Aai Budo Bhaat', 'Gaye Holud', 'Subho Drishti', 'Sindoor Daan']
    },
    southindian: {
        name: 'South Indian',
        description: 'Rich in heritage, devotion, and morning rituals. Seamlessly plan the Mangalya Dharanam.',
        rituals: ['Nischayathartham', 'Pandhalkal', 'Vratham', 'Mangalya Dharanam', 'Sammandhi Mariyathai']
    }
};

// ── Cinematic wedding carousel images ──
const HERO_SLIDES = [
    {
        url: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=1200',
        alt: 'Bride getting ready — vibrant bridal preparation',
    },
    {
        url: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?auto=format&fit=crop&q=80&w=1200',
        alt: 'Indian wedding pheras — sacred vows around holy fire',
    },
    {
        url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&q=80&w=1200',
        alt: 'Wedding celebration dancing — joyous baarat moments',
    },
    {
        url: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&q=80&w=1200',
        alt: 'Couple at golden hour — romantic wedding portraits',
    },
    {
        url: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?auto=format&fit=crop&q=80&w=1200',
        alt: 'Grand wedding mandap — beautifully decorated ceremony stage',
    },
];

const GettingStarted: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [activeCulture, setActiveCulture] = useState('punjabi');
    const cultureData = WEDDING_CULTURES[activeCulture];

    // Auto-advance carousel every 5 seconds
    const nextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, []);

    useEffect(() => {
        const timer = setInterval(nextSlide, 5000);
        return () => clearInterval(timer);
    }, [nextSlide]);

    // Preload first image immediately, and other images after a delay to optimize page load
    useEffect(() => {
        const firstImg = new Image();
        firstImg.src = HERO_SLIDES[0].url;

        const timer = setTimeout(() => {
            HERO_SLIDES.slice(1).forEach((slide) => {
                const img = new Image();
                img.src = slide.url;
            });
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="landing-page">
            <section className="hero-section hero-cinematic" id="hero">
                {/* ── Background Carousel ── */}
                <div className="hero-carousel" aria-hidden="true">
                    {HERO_SLIDES.map((slide, index) => (
                        <div
                            key={index}
                            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
                            style={{ backgroundImage: `url(${slide.url})` }}
                            role="img"
                            aria-label={slide.alt}
                        />
                    ))}
                    <div className="hero-cinematic-overlay" />
                </div>

                {/* ── Slide Indicators ── */}
                <div className="hero-indicators">
                    {HERO_SLIDES.map((_, i) => (
                        <button
                            key={i}
                            className={`hero-indicator ${i === currentSlide ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(i)}
                            aria-label={`Go to slide ${i + 1}`}
                        />
                    ))}
                </div>

                {/* ── Hero Content ── */}
                <div className="hero-content-cinematic">
                    <div className="badge hero-badge-glass">ELITE WEDDING MANAGEMENT</div>
                    <h1 className="hero-title-cinematic">
                        From <span className="highlight">Mahurat</span> to <span className="highlight">Mandap.</span>
                        <br />
                        All in One Place.
                    </h1>
                    <p className="hero-subtitle-cinematic">
                        Plan, manage, and celebrate — every ritual, every guest, every detail — 
                        from a single, elegant platform built for modern Indian weddings.
                    </p>
                    <div className="hero-actions hero-actions-dual">
                        <Link
                            to={isAuthenticated ? '/welcome' : '/register'}
                            className="hero-cta-primary"
                            id="cta-start-planning"
                        >
                            Start Planning Your Wedding
                        </Link>
                        <Link
                            to="/marketplace"
                            className="hero-cta-secondary"
                            id="cta-explore-marketplace"
                        >
                            Explore the Marketplace
                        </Link>
                    </div>
                </div>
            </section>

            <section className="features-section">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Everything You Need for the Perfect Wedding</h2>
                        <p className="section-subtitle">Discover intelligent solutions tailored for every aspect of your celebration.</p>
                    </div>

                    <div className="features-grid">
                        <Link to="/marketplace" className="feature-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="feature-icon">🛍️</div>
                            <h3>Shop &amp; Customize</h3>
                            <p>Buy ritual materials, beauty products, or custom-design your dream outfit.</p>
                        </Link>
                        
                        <Link to="/admin/ceremonies" className="feature-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="feature-icon">🪔</div>
                            <h3>Culture &amp; Traditions</h3>
                            <p>Find your Shadi Mahurat, explore regional ceremonies, and discover traditional wedding songs.</p>
                        </Link>
                        
                        <Link to="/admin/shadi-wala" className="feature-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="feature-icon">✉️</div>
                            <h3>Design Studio</h3>
                            <p>Create stunning digital invitations, browse physical cards, and plan fun guest activities.</p>
                        </Link>
                    </div>
                </div>
            </section>


            {/* ── Interactive Culture Widget ── */}
            <section className="culture-widget-section" style={{ padding: '5rem 0', background: '#F9F5F1' }}>
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Plan Your Unique Traditions</h2>
                        <p className="section-subtitle">Select your wedding style to see a sneak peek of the rituals we can help you manage.</p>
                    </div>

                    <div className="culture-widget-container" style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', borderRadius: '16px', padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        
                        <div className="culture-tabs" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            {Object.entries(WEDDING_CULTURES).map(([key, data]) => (
                                <button
                                    key={key}
                                    onClick={() => setActiveCulture(key)}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '30px',
                                        border: '1px solid #C5A059',
                                        background: activeCulture === key ? '#C5A059' : 'transparent',
                                        color: activeCulture === key ? '#fff' : '#C5A059',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    {data.name} Wedding
                                </button>
                            ))}
                        </div>

                        <div className="culture-preview" style={{ background: '#FAF9F6', border: '1px solid #EAE3D9', borderRadius: '12px', padding: '2rem', textAlign: 'center' }}>
                            <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.8rem', color: '#3E1F24', marginBottom: '0.5rem' }}>
                                The {cultureData.name} Celebration
                            </h3>
                            <p style={{ color: '#666', marginBottom: '2rem' }}>{cultureData.description}</p>
                            
                            <div className="rituals-grid" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                {cultureData.rituals.map((ritual, idx) => (
                                    <span key={idx} style={{ background: '#fff', border: '1px solid #EAE3D9', padding: '0.5rem 1rem', borderRadius: '8px', color: '#333', fontSize: '0.9rem', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        ✓ {ritual}
                                    </span>
                                ))}
                                <span style={{ background: '#F0EAE2', border: '1px dashed #C5A059', padding: '0.5rem 1rem', borderRadius: '8px', color: '#C5A059', fontSize: '0.9rem', fontWeight: 600 }}>
                                    + Many more...
                                </span>
                            </div>

                            <Link to="/login" style={{ display: 'inline-block', padding: '0.75rem 2rem', background: '#3E1F24', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, transition: 'background 0.3s' }}>
                                Sign Up to Build Your Full {cultureData.name} Itinerary
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Social Proof / Testimonials ── */}
            <section className="testimonials-section" style={{ padding: '5rem 0', background: '#fff' }}>
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Loved by Thousands of Couples</h2>
                        <p className="section-subtitle">See how Shadi Gram is transforming wedding planning across India.</p>
                    </div>

                    <div className="testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
                        
                        <div className="testimonial-card" style={{ background: '#FAF9F6', padding: '2rem', borderRadius: '16px', border: '1px solid #EAE3D9', position: 'relative' }}>
                            <div style={{ fontSize: '3rem', color: '#C5A059', position: 'absolute', top: '0.5rem', right: '1.5rem', opacity: 0.2, fontFamily: 'serif' }}>"</div>
                            <div style={{ display: 'flex', gap: '4px', color: '#C5A059', marginBottom: '1rem', fontSize: '1.2rem' }}>★★★★★</div>
                            <p style={{ color: '#444', fontStyle: 'italic', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                "Planning our big Punjabi wedding felt impossible until we found Shadi Gram. The guest RSVP tracking and timeline tools saved us countless hours!"
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#EAE3D9', overflow: 'hidden' }}>
                                    <img src="https://images.unsplash.com/photo-1543604130-9dbcd9d750c1?auto=format&fit=crop&w=100&q=80" alt="Couple" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#3E1F24' }}>Priya &amp; Rahul</div>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Delhi, India</div>
                                </div>
                            </div>
                        </div>

                        <div className="testimonial-card" style={{ background: '#FAF9F6', padding: '2rem', borderRadius: '16px', border: '1px solid #EAE3D9', position: 'relative' }}>
                            <div style={{ fontSize: '3rem', color: '#C5A059', position: 'absolute', top: '0.5rem', right: '1.5rem', opacity: 0.2, fontFamily: 'serif' }}>"</div>
                            <div style={{ display: 'flex', gap: '4px', color: '#C5A059', marginBottom: '1rem', fontSize: '1.2rem' }}>★★★★★</div>
                            <p style={{ color: '#444', fontStyle: 'italic', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                "We found the perfect Maharashtrian Pandit and decorator through the marketplace. The verified vendors gave us total peace of mind."
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#EAE3D9', overflow: 'hidden' }}>
                                    <img src="https://images.unsplash.com/photo-1522864603953-29a32c2a0d78?auto=format&fit=crop&w=100&q=80" alt="Couple" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#3E1F24' }}>Anjali &amp; Rohan</div>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Mumbai, India</div>
                                </div>
                            </div>
                        </div>

                        <div className="testimonial-card" style={{ background: '#FAF9F6', padding: '2rem', borderRadius: '16px', border: '1px solid #EAE3D9', position: 'relative' }}>
                            <div style={{ fontSize: '3rem', color: '#C5A059', position: 'absolute', top: '0.5rem', right: '1.5rem', opacity: 0.2, fontFamily: 'serif' }}>"</div>
                            <div style={{ display: 'flex', gap: '4px', color: '#C5A059', marginBottom: '1rem', fontSize: '1.2rem' }}>★★★★★</div>
                            <p style={{ color: '#444', fontStyle: 'italic', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                                "The custom digital invites were stunning! We easily designed and sent them via WhatsApp, and the built-in map links guided everyone perfectly."
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '45px', height: '45px', borderRadius: '50%', background: '#EAE3D9', overflow: 'hidden' }}>
                                    <img src="https://images.unsplash.com/photo-1621245059639-688ea56f0bc4?auto=format&fit=crop&w=100&q=80" alt="Couple" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#3E1F24' }}>Sneha &amp; Amit</div>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>Bangalore, India</div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── Free Trial CTA ── */}
            <section className="free-trial-section">
                <div className="container">
                    <div className="free-trial-card">
                        <div className="free-trial-icon">🎁</div>
                        <h2 className="free-trial-title">Try Shadi Gram <span className="highlight">Free!</span></h2>
                        <p className="free-trial-subtitle">
                            Get free trial credentials instantly. Contact us on WhatsApp — we'd love to help you plan the perfect wedding!
                        </p>
                        <div className="whatsapp-buttons">
                            <a
                                href="https://wa.me/917999782728?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20get%20free%20trial%20credentials."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-btn"
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                                79997 82728
                            </a>
                            <a
                                href="https://wa.me/919826017177?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20get%20free%20trial%20credentials."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="whatsapp-btn"
                            >
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                                98260 17177
                            </a>
                        </div>
                        <p className="free-trial-note">💬 Message us on WhatsApp for instant access</p>
                    </div>
                </div>
            </section>

            {/* ── Floating WhatsApp Button ── */}
            <a
                href="https://wa.me/917999782728?text=Hi%20Shadi%20Gram!%20I%20would%20like%20to%20get%20free%20trial%20credentials."
                target="_blank"
                rel="noopener noreferrer"
                className="floating-whatsapp"
                title="Chat on WhatsApp for Free Trial"
            >
                <svg viewBox="0 0 24 24" width="28" height="28" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
            </a>

            {/* ── Vendor Growth Banner ── */}
            <div style={{ background: '#3E1F24', padding: '1.5rem 0' }}>
                <div className="container" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', textAlign: 'center' }}>
                    <p style={{ color: '#F9F5F1', fontSize: '1.1rem', margin: 0, fontWeight: 500 }}>
                        Are you a boutique, designer, or wedding vendor? <span style={{ color: '#C5A059', fontWeight: 600 }}>Grow your business with Shadi Gram.</span>
                    </p>
                    <Link to="/agency/register" style={{ background: '#C5A059', color: '#fff', padding: '0.6rem 1.5rem', borderRadius: '30px', fontWeight: 700, textDecoration: 'none', transition: 'background 0.3s' }}>
                        Register Here
                    </Link>
                </div>
            </div>

            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <img src="/shadigram-logo-bold.png" alt="Shadi Gram logo" className="footer-logo" />
                        </div>
                        <p>&copy; {new Date().getFullYear()} Shadi Gram — Shadi Wali App. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default GettingStarted;
